/**
 * "Ask the agent about this article" (plan part F3).
 *
 * A drop-in for the helpdesk article page, which is part B and does not exist yet.
 * The article page mounts this and calls {@link useHelpArticleAgentContext} once; nothing
 * else is needed on that side.
 *
 * It targets the article rather than prefilling the composer, mirroring the per-answer
 * "ask agent" button on PdfAnswerItem. The user still writes their own question; the
 * article rides along as page context so the agent knows what "this" means. Prefilling
 * text would put words in the user's mouth and, worse, make the transcript read as though
 * they had asked something they did not.
 */

import { useCallback, useEffect } from "react"

import { Button } from "@/components/ui/button"
import { PERMISSIONS } from "@/constants/permissions"
import { useAgentChatEnabled } from "@/features/agent-chat/useAgentChatEnabled"
import { useRegisterAgentContext } from "@/features/agent-chat/context/AgentPageContext"
import { useAgentChatContext } from "@/contexts/AgentChatContext"
import { useFeatureFlag } from "@/contexts/FeatureFlagContext"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useLayoutStore } from "@/store/layoutStore"

export interface AskAgentAboutHelpArticleProps {
  articleId: string
  title: string
  className?: string
}

/**
 * Register the open article as page context.
 *
 * Call once from the article page, not from the button: page context is a property of
 * the page and must be registered whether or not the user ever presses anything, so the
 * agent can answer "what does this say about X" the moment the panel is opened from the
 * app chrome.
 */
export function useHelpArticleAgentContext(
  articleId: string,
  route: string,
): void {
  const { clearTargetedHelpArticle } = useAgentChatContext()

  useRegisterAgentContext({
    route,
    entities: { help_article_id: articleId },
  })

  // The chip must not outlive the context it describes. useRegisterAgentContext clears
  // the article from the request on unmount, so without this the chip kept claiming
  // "Artikel: X" on the next screen while the agent no longer had X — the two would
  // disagree and the visible one would be the wrong one. Keyed on articleId so moving
  // between articles clears it too.
  useEffect(
    () => clearTargetedHelpArticle,
    [articleId, clearTargetedHelpArticle],
  )
}

export const AskAgentAboutHelpArticle = ({
  articleId,
  title,
  className,
}: AskAgentAboutHelpArticleProps) => {
  const { translate } = useTranslation()
  const { setTargetedHelpArticle } = useAgentChatContext()
  const { openChatPanel } = useLayoutStore()

  // Three flags and a permission, each carrying its own weight. ENABLE_SIDECHAT decides
  // whether the docked panel exists at all; agentChatEnabled decides whether that panel
  // forwards page context once open, without which this would open a panel that never
  // learns which article the reader is on; HELPDESK gates the feature itself.
  //
  // chat.use is a permission rather than a flag, and it is the one that is easy to
  // forget: a reader can reach an article without being allowed to use the assistant,
  // and a button into a permission error is worse than no button. Same check
  // HelpdeskEmptyState makes before offering the agent, for the same reason.
  const sidechatEnabled = useFeatureFlag("ENABLE_SIDECHAT")
  const agentChatEnabled = useAgentChatEnabled()
  const helpdeskEnabled = useFeatureFlag("HELPDESK")
  const { hasPermission } = useUser()

  const handleClick = useCallback(() => {
    setTargetedHelpArticle({ articleId, title })
    openChatPanel()
  }, [setTargetedHelpArticle, openChatPanel, articleId, title])

  if (!sidechatEnabled || !agentChatEnabled || !helpdeskEnabled) return null
  if (!hasPermission(PERMISSIONS.CHAT_USE)) return null

  return (
    <Button
      variant="outline"
      size="sm"
      onClick={handleClick}
      className={className}
      data-testid="ask-agent-about-help-article"
    >
      {translate("AI_CHAT/ASK_ABOUT_HELP_ARTICLE")}
    </Button>
  )
}
